<script setup lang="ts">
import { useSupabaseAuth } from './composables/useSupabaseAuth'
import { RouterView, RouterLink, useRouter } from 'vue-router'
import { Menu, X, Mic, Settings, LogOut, Crown, Star, Zap, Shield } from 'lucide-vue-next'
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UpgradeDialog from './components/UpgradeDialog.vue'
import AuthModal from './components/AuthModal.vue'
import ThemeToggle from './components/ThemeToggle.vue'
import LanguageSelector from './components/LanguageSelector.vue'
import { useSubscriptionStore } from './stores/subscription'
import { callEdgeFunction } from './config/api'

useI18n() // Initialize i18n for global $t in templates
const { isSignedIn, user, signOut: supabaseSignOut } = useSupabaseAuth()
const router = useRouter()
const mobileMenuOpen = ref(false)
const showUpgradeDialog = ref(false)
const showAuthModal = ref(false)
const subscriptionStore = useSubscriptionStore()
const isAdmin = ref(false)

// Open sign in - show auth modal directly (no navigation needed)
function openSignIn() {
  showAuthModal.value = true
}

// Close auth modal
function closeAuthModal() {
  showAuthModal.value = false
}

// Sign out
async function signOut() {
  await supabaseSignOut()
  isAdmin.value = false
}

// Check if user is admin
async function checkAdminStatus() {
  try {
    const data = await callEdgeFunction<{ isAdmin: boolean }>('admin', {
      body: { action: 'check' }
    })
    isAdmin.value = data.isAdmin
  } catch {
    isAdmin.value = false
  }
}

// Redirect to dashboard if signed in and on landing page, and check admin status
watch(isSignedIn, (signedIn) => {
  if (signedIn) {
    // Close auth modal if open
    showAuthModal.value = false
    if (router.currentRoute.value.path === '/') {
      router.push('/dashboard')
    }
    checkAdminStatus()
  } else {
    isAdmin.value = false
    const requiresAuth = router.currentRoute.value.matched.some(record => record.meta.requiresAuth)
    if (requiresAuth) {
      router.replace({ name: 'landing' })
    }
  }
}, { immediate: true })

// Helper to get user email
const userEmail = computed(() => user.value?.email ?? '')
const userName = computed(() => {
  const metadata = user.value?.user_metadata
  return metadata?.full_name || metadata?.name || userEmail.value
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-dark-bg flex flex-col transition-colors">
    <!-- Navigation -->
    <nav class="bg-white dark:bg-dark-surface border-b border-gray-100 dark:border-dark-border transition-colors">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <!-- Logo -->
          <div class="flex items-center">
            <RouterLink :to="isSignedIn ? '/dashboard' : '/'" class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Mic class="w-4 h-4 text-white" />
              </div>
              <span class="text-xl font-semibold text-gray-900 dark:text-white">WunderMemos</span>
            </RouterLink>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center space-x-4">
            <!-- Signed In State -->
            <template v-if="isSignedIn">
              <RouterLink
                v-if="isAdmin"
                to="/admin"
                class="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                <Shield class="w-4 h-4" />
                {{ $t('nav.admin') }}
              </RouterLink>
              <RouterLink
                to="/statistics"
                class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                {{ $t('nav.statistics') }}
              </RouterLink>
              <RouterLink
                to="/support"
                class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                {{ $t('nav.support') }}
              </RouterLink>
              <!-- Upgrade button for free users only -->
              <button
                v-if="!subscriptionStore.isPaid"
                @click="showUpgradeDialog = true"
                class="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-amber-900 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow"
              >
                <Crown class="w-4 h-4" />
                {{ $t('nav.upgrade') }}
              </button>
              <RouterLink
                to="/settings"
                class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                <Settings class="w-5 h-5" />
              </RouterLink>
              <!-- Theme Toggle and Language Selector -->
              <ThemeToggle />
              <LanguageSelector />
              <div class="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-200 dark:border-dark-border">
                <!-- Tier badge with user name (clickable to go to subscription page) -->
                <router-link
                  v-if="subscriptionStore.isPaid"
                  to="/subscription"
                  class="flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-semibold transition-all hover:shadow-md cursor-pointer"
                  :class="{
                    'bg-gradient-to-r from-blue-400 to-blue-500 text-blue-900 hover:from-blue-500 hover:to-blue-600': subscriptionStore.tier === 'basic',
                    'bg-gradient-to-r from-purple-400 to-purple-500 text-purple-900 hover:from-purple-500 hover:to-purple-600': subscriptionStore.tier === 'standard',
                    'bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 hover:from-amber-500 hover:to-amber-600': subscriptionStore.tier === 'premium'
                  }"
                  title="Click to manage subscription"
                >
                  <Zap v-if="subscriptionStore.tier === 'basic'" class="w-4 h-4" />
                  <Star v-else-if="subscriptionStore.tier === 'standard'" class="w-4 h-4" />
                  <Crown v-else class="w-4 h-4" />
                  {{ userName }}
                </router-link>
                <button
                  v-else
                  @click="showUpgradeDialog = true"
                  class="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                  title="Click to upgrade"
                >
                  {{ userEmail }}
                </button>
                <button
                  @click="signOut"
                  class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <LogOut class="w-5 h-5" />
                </button>
              </div>
            </template>

            <!-- Signed Out State -->
            <template v-else>
              <RouterLink
                to="/support"
                class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                {{ $t('nav.support') }}
              </RouterLink>
              <!-- Theme Toggle and Language Selector for signed out users -->
              <ThemeToggle />
              <LanguageSelector />
              <button
                @click="openSignIn"
                class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
              >
                {{ $t('nav.signIn') }}
              </button>
            </template>
          </div>

          <!-- Mobile menu button -->
          <div class="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <LanguageSelector />
            <button @click="mobileMenuOpen = !mobileMenuOpen" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              <Menu v-if="!mobileMenuOpen" class="w-6 h-6" />
              <X v-else class="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden border-t border-gray-100 dark:border-dark-border">
        <div class="px-4 py-4 space-y-3">
          <!-- Signed In State (Mobile) -->
          <template v-if="isSignedIn">
            <RouterLink
              v-if="isAdmin"
              to="/admin"
              class="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
              @click="mobileMenuOpen = false"
            >
              <Shield class="w-4 h-4" />
              {{ $t('nav.admin') }}
            </RouterLink>
            <RouterLink
              to="/statistics"
              class="block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
              @click="mobileMenuOpen = false"
            >
              {{ $t('nav.statistics') }}
            </RouterLink>
            <RouterLink
              to="/settings"
              class="block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
              @click="mobileMenuOpen = false"
            >
              {{ $t('nav.settings') }}
            </RouterLink>
            <RouterLink
              to="/support"
              class="block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
              @click="mobileMenuOpen = false"
            >
              {{ $t('nav.support') }}
            </RouterLink>
            <!-- Upgrade button for free users only (mobile) -->
            <button
              v-if="!subscriptionStore.isPaid"
              @click="() => { showUpgradeDialog = true; mobileMenuOpen = false }"
              class="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-amber-900 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm mt-2"
            >
              <Crown class="w-4 h-4" />
              {{ $t('nav.upgrade') }}
            </button>
            <div class="pt-3 border-t border-gray-100 dark:border-dark-border mt-3">
              <!-- Tier badge with user name (mobile, clickable to go to subscription page) -->
              <router-link
                v-if="subscriptionStore.isPaid"
                to="/subscription"
                @click="mobileMenuOpen = false"
                class="flex items-center gap-2 mb-2"
              >
                <span
                  class="flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-semibold transition-all hover:shadow-md"
                  :class="{
                    'bg-gradient-to-r from-blue-400 to-blue-500 text-blue-900': subscriptionStore.tier === 'basic',
                    'bg-gradient-to-r from-purple-400 to-purple-500 text-purple-900': subscriptionStore.tier === 'standard',
                    'bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900': subscriptionStore.tier === 'premium'
                  }"
                >
                  <Zap v-if="subscriptionStore.tier === 'basic'" class="w-4 h-4" />
                  <Star v-else-if="subscriptionStore.tier === 'standard'" class="w-4 h-4" />
                  <Crown v-else class="w-4 h-4" />
                  {{ subscriptionStore.tierLabel }}
                </span>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ userEmail }}</span>
              </router-link>
              <button
                v-else
                @click="() => { showUpgradeDialog = true; mobileMenuOpen = false }"
                class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2 block"
              >
                {{ userEmail }}
              </button>
              <button
                @click="() => { signOut(); mobileMenuOpen = false }"
                class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
              >
                {{ $t('nav.signOut') }}
              </button>
            </div>
          </template>

          <!-- Signed Out State (Mobile) -->
          <template v-else>
            <RouterLink
              to="/support"
              class="block text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
              @click="mobileMenuOpen = false"
            >
              {{ $t('nav.support') }}
            </RouterLink>
            <button
              @click="() => { openSignIn(); mobileMenuOpen = false }"
              class="block w-full text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2 text-sm font-medium"
            >
              {{ $t('nav.signIn') }}
            </button>
          </template>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-1">
      <RouterView />
    </main>

    <!-- Footer -->
    <footer class="border-t border-gray-100 dark:border-dark-border py-6 bg-white dark:bg-dark-surface transition-colors">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row items-center justify-between gap-4">
          <div class="flex items-center space-x-2">
            <div class="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center">
              <Mic class="w-3 h-3 text-white" />
            </div>
            <span class="font-semibold text-gray-900 dark:text-white">WunderMemos</span>
          </div>
          <div class="flex items-center gap-6 text-sm">
            <RouterLink to="/support" class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">{{ $t('footer.support') }}</RouterLink>
            <RouterLink to="/privacy" class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">{{ $t('footer.privacyPolicy') }}</RouterLink>
            <RouterLink to="/impressum" class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">{{ $t('footer.impressum') }}</RouterLink>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ $t('footer.copyright', { year: new Date().getFullYear() }) }}
          </p>
        </div>
      </div>
    </footer>

    <!-- Upgrade Dialog -->
    <UpgradeDialog
      :show="showUpgradeDialog"
      @close="showUpgradeDialog = false"
    />

    <!-- Auth Modal (for sign in from header) -->
    <AuthModal
      :isOpen="showAuthModal"
      @close="closeAuthModal"
    />
  </div>
</template>
